import { Injectable, Signal, signal } from '@angular/core';
import {
  AppUpdate,
  AppUpdateResult,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';
import { LoggingService } from './logging.service';
import { TelemetryService } from './telemetry.service';
import { ToastController } from 'providers/toast-controller';
import { LL } from 'i18n/i18n.service';
import { AppEnvironment } from 'environments/environment';
import { androidPlayStoreUrlForEnv } from '@cf-shared/data-model/app-install-urls';
import { openExternalLink } from 'util/open-external-link';
// AppVersion is now consumed via TelemetryService.trackAppUpdateEvent,
// which derives current_version internally — no direct import needed.
import {
  AndroidRequiredUpdateFlow,
  AndroidUpdateTapAction,
  chooseAndroidRequiredUpdateFlow,
  chooseAndroidUpdateTapAction,
  classifyAndroidPlayUnreachable,
  clearInProgressVersion,
  isExpectedAndroidInstallError,
  isExpectedAndroidStartRejection,
  mapAndroidInstallState,
  makeInProgressVersionState,
  mapAndroidPluginInfo,
  parseAndroidInstallErrorCode,
  recordVersionSeenWhileDownloading,
  recordVersionUserStarted,
  UpdateInstallState,
  VersionCheckBackend,
  VersionCheckResult,
} from './version-check.dispatch';

// Android backend for VersionCheckService. Wraps the
// `@capawesome/capacitor-app-update` plugin, which is the Capacitor
// wrapper around Google Play's In-App Updates API.
//
// The card's Update button uses Play's *flexible* flow: the download
// happens in the background while the user keeps using the app. The
// home-dashboard card mirrors that progress through three states —
// available (idle), downloading, and ready-to-restart — driven by this
// backend's `installState` signal. The "Please update" alert
// shown below config/MinimumSupportedVersion (#6770) uses Play's
// *immediate* flow (Play's own full-screen download, which restarts the
// app) when Play allows it, and opens the Play Store listing otherwise:
// `startRequiredUpdateAsync`.
//
// See design-docs/in-app-update-prompt.md §Per-Platform Backends.

// What Play said on one successful getAppUpdateInfo round-trip.
interface LastPlayInfo {
  // the version advertised, or null when no update is available.
  // startFlexibleFlowAsync snapshots it for the download it starts.
  availableVersion: string | null;
  // whether Play said the flexible flow may run. startFlexibleFlowAsync
  // reads it to tell a NOT_ALLOWED caused by the phone's state from one
  // that is not explained.
  flexibleAllowed: boolean | undefined;
  // whether Play said the immediate flow may run. startRequiredUpdateAsync
  // uses it to choose between the two flows (#6770).
  immediateAllowed: boolean | undefined;
}

@Injectable({ providedIn: 'root' })
export class VersionCheckAndroidBackend implements VersionCheckBackend {

  // Live install-progress state the card binds to. Starts Idle and is
  // moved by both the flexible-update listener (live, in-session) and
  // getStatusAsync (cold start / resume that lands mid-download).
  private _installState = signal<UpdateInstallState>(UpdateInstallState.Idle);
  readonly installState: Signal<UpdateInstallState> = this._installState.asReadonly();

  // what Play said on the last getAppUpdateInfo round-trip, or null when
  // that read failed (or has not run). Assigned whole in getStatusAsync, so
  // a failed read can never leave one stale answer behind. The dispatcher
  // caches the public-API result but does not pass it back into the
  // backend, so we keep our own copy here.
  private lastPlayInfo: LastPlayInfo | null = null;

  // the version that is currently mid-download, for the completed
  // telemetry's label. Kept separate from lastPlayInfo; its write rule is
  // documented on InProgressVersionState.
  private inProgress = makeInProgressVersionState();

  // owns the flexible-update state listener handle so the listener
  // cannot leak across separate update attempts. capacitor listeners
  // persist across navigations otherwise and would double-fire on each
  // subsequent state transition.
  private flexListener: { remove(): Promise<void> } | null = null;

  // set synchronously while addListener's promise is in flight, so two
  // concurrent attach callers (e.g. an unthrottled Settings query
  // overlapping a resume refresh) can't both pass the `!flexListener`
  // check and attach twice, leaking the first handle. flexListener
  // alone can't guard this — it isn't assigned until the await resolves.
  private isAttachingFlexListener = false;

  constructor(
    private logging: LoggingService,
    private telemetry: TelemetryService,
    private toastCtrl: ToastController,
  ) {}

  async getStatusAsync(): Promise<VersionCheckResult> {
    try {
      const info = await AppUpdate.getAppUpdateInfo();
      // delegate the availability → updateAvailable mapping + the
      // missing-versionCode 'pending' fallback to the pure helper so
      // the decision table stays unit-testable without pulling
      // Capacitor into mocha. Note: this now treats UPDATE_IN_PROGRESS
      // as "updateAvailable" too, so the card stays visible while a
      // flexible update downloads.
      const result = mapAndroidPluginInfo({
        updateAvailability: info.updateAvailability,
        availableVersionCode: info.availableVersionCode,
      });
      this.lastPlayInfo = {
        availableVersion: result.updateAvailable ? result.availableVersion : null,
        flexibleAllowed: info.flexibleUpdateAllowed,
        immediateAllowed: info.immediateUpdateAllowed,
      };

      // reflect any in-flight flexible download into the card's mode so
      // a cold start or resume that lands mid-download shows
      // "Downloading…" / Restart rather than the plain Update button.
      // Only derive a non-Idle mode when there is actually a card to
      // show (updateAvailable): otherwise the install signal and the
      // card-visibility decision could disagree (signal Downloading
      // while the card is hidden).
      const installState = result.updateAvailable
        ? mapAndroidInstallState(info.updateAvailability, info.installStatus)
        : UpdateInstallState.Idle;
      this._installState.set(installState);
      // track the mid-flight version so the Restart tap's completed-
      // telemetry can label itself even when this process never ran
      // startUpdateAsync (download began in a prior session).
      if (result.updateAvailable && installState !== UpdateInstallState.Idle) {
        recordVersionSeenWhileDownloading(this.inProgress, result.availableVersion);
      } else {
        clearInProgressVersion(this.inProgress);
      }
      // a download underway with no live listener (fresh process after
      // a cold start mid-download) would otherwise never advance to
      // Ready — re-attach so we still catch the DOWNLOADED transition.
      if (installState === UpdateInstallState.Downloading && !this.flexListener) {
        await this.attachFlexListenerAsync();
        // close the snapshot→attach gap: if DOWNLOADED (or a failure)
        // landed in the window between the getAppUpdateInfo() read above
        // and the listener attaching, the live listener missed it and the
        // card would be stuck "Downloading…" all session (resume is
        // throttled). re-read once now that the listener is live and
        // reconcile to the true current state.
        await this.reconcileInstallStateAsync();
      }
      return result;
    } catch (err) {
      // surface so chronic Play-Core init failures are visible;
      // return updateAvailable:false so the dashboard degrades to
      // "no card" rather than throwing into the dashboard render.
      // if Play refused because of the phone's state or a non-Play
      // install (#6124), record it at the info tier (capped daily per
      // device) instead: nothing is broken and nobody needs to act.
      // the same holds when Play's update service could not be reached
      // at all (no Play Store app, or its process died mid-call); those
      // carry no code, so `reason` in the context says which it was.
      // every other failure stays a problem.
      const message = (err as Error)?.message;
      const code = parseAndroidInstallErrorCode(message);
      const reason = classifyAndroidPlayUnreachable(message);
      const context = { message, name: (err as Error)?.name, code, ...(reason ? { reason } : {}) };
      if (isExpectedAndroidInstallError(code) || reason !== null) {
        this.logging.trackSilentInfo('VersionCheckAndroidGetInfoFailed', context);
      } else {
        this.logging.trackSilentProblem('VersionCheckAndroidGetInfoFailed', context);
      }
      this.lastPlayInfo = null;
      this._installState.set(UpdateInstallState.Idle);
      return { updateAvailable: false };
    }
  }

  async startUpdateAsync(belowMinimum: boolean): Promise<void> {
    if (!(await this.prepareUpdateStartAsync(belowMinimum))) return;
    await this.startFlexibleFlowAsync();
  }

  // The "Please update" alert's Update button (#6770). A download
  // already running or finished is routed exactly as startUpdateAsync routes it.
  // When nothing is in flight, Play's immediate flow runs if Play allows it;
  // otherwise, or if Play refuses it, the Play Store listing opens, so the button
  // always leads somewhere the user can update.
  async startRequiredUpdateAsync(belowMinimum: boolean): Promise<void> {
    if (!(await this.prepareUpdateStartAsync(belowMinimum))) return;
    switch (chooseAndroidRequiredUpdateFlow(this.lastPlayInfo?.immediateAllowed)) {
      case AndroidRequiredUpdateFlow.Immediate:
        await this.startImmediateFlowAsync();
        return;
      case AndroidRequiredUpdateFlow.StoreListing:
        await this.openPlayStoreListingAsync();
        return;
    }
  }

  // Shared start of both update buttons: re-fetch Play's info, then route a
  // download already in flight. Returns true when nothing is in flight and the
  // caller may start a Play flow.
  private async prepareUpdateStartAsync(belowMinimum: boolean): Promise<boolean> {
    // tear down any prior listener before starting a new flow —
    // protects against stuck listeners from a previous interrupted
    // attempt.
    await this.detachListenerAsync();

    // Play requires a FRESH getAppUpdateInfo() immediately before each
    // startFlexibleUpdate(): the previous attempt consumes the cached
    // AppUpdateInfo, so without this a second tap (e.g. after the user
    // cancelled the Play sheet) returns INFO_MISSING and shows nothing.
    // Reusing getStatusAsync re-primes the plugin AND refreshes
    // lastPlayInfo for the snapshot in startFlexibleFlowAsync. (At a fresh
    // update-start the state is Idle, so getStatusAsync attaches no
    // listener; a download already in progress is routed away below.)
    await this.getStatusAsync();

    // if a download is already running or finished, starting Play's flow
    // again can only fail with NOT_AVAILABLE (the generic error toast the
    // user saw on a second Settings tap). route by the fresh state instead.
    // getStatusAsync has already re-attached the listener for a running
    // download, so the card still advances to Ready.
    switch (chooseAndroidUpdateTapAction(this._installState())) {
      case AndroidUpdateTapAction.ShowDownloading:
        this.toastCtrl.showToast({
          message: LL.app.updateAvailable.downloading(),
          duration: 3000,
        });
        return false;
      case AndroidUpdateTapAction.CompleteUpdate:
        await this.completeUpdateAsync(belowMinimum);
        return false;
      case AndroidUpdateTapAction.StartFlow:
        return true;
    }
  }

  // Play's immediate flow: its own full-screen download, after which Play
  // restarts the app on the new version. So no `completed` event fires for it;
  // the next start's current_version shows the update landed.
  private async startImmediateFlowAsync(): Promise<void> {
    // a throw is an internal plugin / Play-Core error. report it, then still send
    // the user to the store: the owner asked that this button always lead there
    let result: AppUpdateResult;
    try {
      result = await AppUpdate.performImmediateUpdate();
    } catch (err) {
      this.logging.trackSilentProblem('VersionCheckAndroidImmediateFailed', {
        message: (err as Error)?.message,
        name: (err as Error)?.name,
      });
      await this.openPlayStoreListingAsync();
      return;
    }
    // OK: Play has taken over. CANCELED: the user backed out of Play's screen,
    // which is their choice, not an error.
    if (result.code === AppUpdateResultCode.OK || result.code === AppUpdateResultCode.CANCELED) return;
    // FAILED / NOT_AVAILABLE / NOT_ALLOWED / INFO_MISSING: report it, with the
    // same phone-state split as the flexible flow (#6124), then send the user to
    // the store listing instead, where they can still update
    const immediateUpdateAllowed = this.lastPlayInfo?.immediateAllowed;
    const context = { code: result.code, immediateUpdateAllowed };
    if (isExpectedAndroidStartRejection(result.code, immediateUpdateAllowed)) {
      this.logging.trackSilentInfo('VersionCheckAndroidImmediateRejected', context);
    } else {
      this.logging.trackSilentProblem('VersionCheckAndroidImmediateRejected', context);
    }
    await this.openPlayStoreListingAsync();
  }

  // Opens this environment's Play Store listing, the same way Settings' "Rate the
  // app" row does: Play's own app handles the play.google.com link.
  private async openPlayStoreListingAsync(): Promise<void> {
    await openExternalLink(androidPlayStoreUrlForEnv(AppEnvironment.name));
  }

  // Play's flexible flow: a background download the card follows.
  private async startFlexibleFlowAsync(): Promise<void> {
    // snapshot the available version into a local so it can't drift if
    // a later getStatusAsync replaces lastPlayInfo mid-flow.
    const availableVersion = this.lastPlayInfo?.availableVersion ?? 'pending';

    // attach the listener first so the contract is symmetric with the
    // catch-block cleanup. (no race against the JS event loop: the
    // Play-Core listener only emits after startFlexibleUpdate runs,
    // which is after this attach has resolved.)
    await this.attachFlexListenerAsync();

    let result: AppUpdateResult;
    try {
      result = await AppUpdate.startFlexibleUpdate();
    } catch (err) {
      // start threw → tear down the listener we just attached so we
      // don't leak it across a no-op startUpdate, then rethrow so the
      // dashboard's catch routes through trackAbnormalException + the
      // user-visible toast path. Per CLAUDE.md "unexpected conditions
      // must surface".
      await this.detachListenerAsync();
      throw err;
    }

    // on the normal Play outcomes startFlexibleUpdate RESOLVES with a
    // result code (it does not throw to signal user-cancel / not-allowed
    // / info-missing) — so inspect the code rather than assuming success.
    // (An internal plugin / Play-Core error can still throw, which is
    // what the try/catch above handles.)
    if (result.code === AppUpdateResultCode.OK) {
      // user accepted; the flexible listener now drives installState
      // (PENDING/DOWNLOADING → Downloading, DOWNLOADED → Ready).
      recordVersionUserStarted(this.inProgress, availableVersion);
      return;
    }

    // any non-OK outcome means the download did not begin → detach the
    // unused listener.
    await this.detachListenerAsync();

    if (result.code === AppUpdateResultCode.CANCELED) {
      // user dismissed the Play sheet without accepting. Leave the card
      // in its Idle "Update available" mode so the next tap re-opens
      // the flow (this is the path the re-fetch above keeps working).
      // No telemetry/toast — a deliberate cancel is not an error.
      return;
    }

    // FAILED / NOT_AVAILABLE / NOT_ALLOWED / INFO_MISSING: the flow
    // could not start. Surface it (Sentry + toast) rather than leaving
    // a dead button with no feedback. Per CLAUDE.md "unexpected
    // conditions must surface".
    // if Play had already said the flexible flow is not allowed, the
    // NOT_ALLOWED answer describes the phone's state (#6124's reasoning),
    // so it is recorded as info. the toast still tells the user it failed.
    const flexibleUpdateAllowed = this.lastPlayInfo?.flexibleAllowed;
    const context = { code: result.code, flexibleUpdateAllowed };
    if (isExpectedAndroidStartRejection(result.code, flexibleUpdateAllowed)) {
      this.logging.trackSilentInfo('VersionCheckAndroidStartFlexibleRejected', context);
    } else {
      this.logging.trackSilentProblem('VersionCheckAndroidStartFlexibleRejected', context);
    }
    this.toastCtrl.showToast({
      message: LL.common.errors.generic(),
      duration: 3000,
    });
  }

  // Finishes a downloaded flexible update by restarting the app. Called
  // when the user taps "Restart" on the Ready card. This is where the
  // app_update.completed telemetry fires now — not on DOWNLOADED — so
  // the funnel records the user's explicit decision to finish.
  async completeUpdateAsync(belowMinimum: boolean): Promise<void> {
    // fire completed telemetry with the version advertised when the
    // download began, so Mixpanel funnels click → completed by version.
    // Per PRD §7 the completed event is bound to the card surface even
    // when the flow was triggered from Settings.
    const availableVersion = this.inProgress.version ?? 'pending';
    this.telemetry.trackAppUpdateEvent('completed', 'card', availableVersion, belowMinimum);
    // ensure the telemetry event leaves the device BEFORE
    // completeFlexibleUpdate restarts the process. without flushAsync
    // the Mixpanel HTTP request is still queued when the process dies
    // and the completed leg of the funnel looks like a 100% drop-off.
    await this.telemetry.flushAsync();
    // detach so the listener doesn't re-fire against the new process
    // post-restart.
    await this.detachListenerAsync();
    try {
      await AppUpdate.completeFlexibleUpdate();
    } catch (err) {
      // record for on-call AND show the user a translated toast so they
      // know to relaunch manually — without the toast they'd be
      // stranded with a downloaded-but-unrestarted update and no UI
      // feedback (per CLAUDE.md "terminal errors should show the user a
      // translated, actionable message").
      this.logging.trackSilentProblem('VersionCheckAndroidCompleteFlexibleFailed', {
        message: (err as Error)?.message,
        name: (err as Error)?.name,
      });
      this.toastCtrl.showToast({
        message: LL.common.errors.generic(),
        duration: 3000,
      });
    }
  }

  // Attaches the flexible-update state listener if one isn't already
  // live (or being attached). Shared by startUpdateAsync (in-session
  // flow) and getStatusAsync (cold-start re-attach).
  private async attachFlexListenerAsync(): Promise<void> {
    if (this.flexListener || this.isAttachingFlexListener) return;
    // claim the slot synchronously so a concurrent caller bails before
    // its own addListener; see the isAttachingFlexListener field comment.
    this.isAttachingFlexListener = true;
    try {
      this.flexListener = await AppUpdate.addListener(
        'onFlexibleUpdateStateChange',
        async state => {
          // a FAILED download is an unexpected condition (a CANCELED one
          // is the user's own choice) — surface it before we collapse
          // both to Idle below, per CLAUDE.md "unexpected conditions
          // must surface".
          if (state.installStatus === FlexibleUpdateInstallStatus.FAILED) {
            this.logging.trackSilentProblem('VersionCheckAndroidFlexibleUpdateFailed', {
              installStatus: state.installStatus,
            });
          }
          // map the raw install status to our card model and apply it.
          // We pass 0 as the availability arg (no UPDATE_IN_PROGRESS
          // hint): a listener event always carries a concrete
          // installStatus, so FAILED / CANCELED (and INSTALLED, which
          // only fires post-restart when the process is already gone)
          // resolve to Idle rather than being forced to Downloading.
          await this.applyInstallStateAsync(mapAndroidInstallState(0, state.installStatus));
        },
      );
    } finally {
      this.isAttachingFlexListener = false;
    }
  }

  // Publishes a freshly-mapped install state coming from a listener event
  // or a reconcile read. Shared by both so the "set signal → forget the
  // in-flight version once the flow ends → detach for any non-Downloading
  // state" tail lives in one place. getStatusAsync does NOT use this — it
  // owns the re-attach path and the first-seen-wins version handling.
  private async applyInstallStateAsync(next: UpdateInstallState): Promise<void> {
    this._installState.set(next);
    // if the flow ended (failed / cancelled → Idle), forget the in-flight
    // version. Ready keeps it — the Restart tap's telemetry needs it.
    if (next === UpdateInstallState.Idle) {
      clearInProgressVersion(this.inProgress);
    }
    // Downloading is the only non-terminal state. Ready waits on the
    // user's Restart tap and Idle means the flow ended, so for both there
    // are no further transitions worth tracking in this process — detach
    // to avoid a leaked / double-firing listener.
    if (next !== UpdateInstallState.Downloading) {
      await this.detachListenerAsync();
    }
  }

  // Re-reads the current install status and applies it to the card.
  // Used right after a cold-start re-attach to recover a terminal
  // transition (DOWNLOADED / failure) that may have fired in the gap
  // between the initial getAppUpdateInfo() snapshot and the listener
  // going live. A failure here is non-fatal — the card simply keeps the
  // pre-reconcile state — so we log and continue rather than throw.
  private async reconcileInstallStateAsync(): Promise<void> {
    try {
      const info = await AppUpdate.getAppUpdateInfo();
      // gate on the same updateAvailable decision getStatusAsync uses, so
      // the install signal can't read Downloading/Ready while the card
      // would be hidden (an availability mapAndroidPluginInfo treats as
      // "no card").
      const reconciled = mapAndroidPluginInfo(info).updateAvailable
        ? mapAndroidInstallState(info.updateAvailability, info.installStatus)
        : UpdateInstallState.Idle;
      await this.applyInstallStateAsync(reconciled);
    } catch (err) {
      this.logging.trackSilentProblem('VersionCheckAndroidReconcileFailed', {
        message: (err as Error)?.message,
        name: (err as Error)?.name,
      });
    }
  }

  private async detachListenerAsync(): Promise<void> {
    const handle = this.flexListener;
    this.flexListener = null;
    if (!handle) return;
    try {
      await handle.remove();
    } catch (err) {
      // listener-removal failure is non-fatal but should be visible
      // — a stuck listener would double-fire on the next attempt.
      this.logging.trackSilentProblem('VersionCheckAndroidDetachListenerFailed', {
        message: (err as Error)?.message,
        name: (err as Error)?.name,
      });
    }
  }
}
